import { useIsBarclay, useLanguage } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'
import { notNull } from '@/utils/FloatNumber'
import { Action0, Action1 } from '@/utils/Types'
import { CartDetail } from '@models/Index'
import { appUtils } from 'lib/AppUtils'
import axios from 'lib/axios'
import { useRouter } from 'next/router'
import { CSSProperties, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'

const ApplePayButton: FC<{
  total: number
  form: UseFormReturn<FieldValues, any>
  disabled?: boolean // !agree || paying
  setDisabled?: Dispatch<SetStateAction<boolean>>
  cartId?: any
  loading?: boolean
  setLoading?: Dispatch<SetStateAction<boolean>>
  setCartDetail?: Dispatch<SetStateAction<CartDetail>>
  updateCartLocal?: Action1<any>
  toastNoAddress?: Action0
  extraCls?: string
  style: CSSProperties
}> = ({ total, form, disabled, setDisabled, cartId, setLoading, setCartDetail, updateCartLocal, toastNoAddress, extraCls, style }) => {
  const { errors: errStr, cart: cartStr, wallet: walletStr } = useLanguage()
  const router = useRouter()
  const isBarclays = useIsBarclay()
  const [isSupported, setSupported] = useState(false)
  const [hover, setHover] = useState(false)
  const isCart = useMemo<boolean>(() => typeof cartId != 'undefined' && cartId != null, [cartId])
  const wrapRef = useRef(null)
  const shadowRef = useRef(null)
  const {
    getValues, // trigger,
    formState,
    reset,
    handleSubmit,
  } = form
  const formInvalid = useMemo<boolean>(() => Object.keys(formState.errors)?.length > 0, [formState])

  useEffect(() => {
    let interval = null
    // @ts-ignore
    if (window['ApplePaySession'] && ApplePaySession.canMakePayments()) {
      setSupported(true)
      if (shadowRef.current?.shadowRoot?.getElementById?.('container')) {
        updateBtnColor(true)
      } else {
        interval = setInterval(() => {
          if (shadowRef.current?.shadowRoot?.getElementById?.('container')) {
            updateBtnColor(true)
            clearInterval(interval)
          }
        }, 100)
      }
    } else {
      setSupported(false)
    }
    return () => {
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (shadowRef.current) {
      updateBtnColor()
    }
  }, [shadowRef.current, disabled, hover, formInvalid])

  const updateBtnColor = (init?: boolean) => {
    if (disabled || formInvalid) {
      // wrapRef.current.removeEventListener('click', onClick)
      shadowRef.current.removeEventListener('click', onClick)
      wrapRef.current.removeEventListener('mouseover', () => setHover(true))
      wrapRef.current.removeEventListener('mouseleave', () => setHover(false))
    } else {
      // wrapRef.current.addEventListener('click', onClick)
      shadowRef.current.addEventListener('click', onClick)
      wrapRef.current.addEventListener('mouseover', () => setHover(true))
      wrapRef.current.addEventListener('mouseleave', () => setHover(false))
    }
    // reset shadow tree elements style, and customize style in globals.css
    const svg = shadowRef.current.shadowRoot?.getElementById?.('svg-text')
    if (init) {
      const style = svg?.getElementsByTagName?.('style')?.[0]
      if (style) {
        style.remove()
      }
      const logo = svg?.getElementsByTagName?.('svg')?.[0]
      if (logo) {
        logo.setAttribute('x', '78')
        logo.setAttribute('y', '3')
        logo.setAttribute('width', '40')
        logo.setAttribute('height', '24')
      }
    }
    const btn = shadowRef.current.shadowRoot?.getElementById?.('container')?.parentElement
    if (btn) {
      if (init) {
        btn.parentElement?.setAttribute?.('part', 'wrapper')
        btn.setAttribute('part', 'btn') // see globals.css apple-pay-button::part(btn)
      }
      btn.toggleAttribute('disabled', disabled || formInvalid) // disable brn func
    }
    const text = svg?.getElementsByTagName?.('text')?.[0]
    if (text) {
      if (init) {
        text.removeAttribute('style')
        text.removeAttribute('height')
        text.removeAttribute('width')
        text.removeAttribute('textLength')
        text.setAttribute('x', '0')
        text.setAttribute('part', 'text')
      }
      text.setAttribute('fill', disabled || hover ? '#fff' : '#000') // white : black
    }
  }

  const getPaymentData = event => {
    const paymentDataString = JSON.stringify(event?.payment?.token?.paymentData)
    const paymentDataBase64 = btoa(paymentDataString)
    return paymentDataBase64
  }

  const onClick = useCallback(
    evt => {
      // const valid = await trigger(undefined, { shouldFocus: true })
      if (typeof toastNoAddress == 'function' && !getValues('address')) {
        toastNoAddress()
        evt?.preventDefault?.()
        evt?.stopImmediatePropagation?.()
        return
      }
      if (!notNull(total) || !(Number(total) > 0) || formInvalid) {
        evt?.preventDefault?.()
        evt?.stopImmediatePropagation?.()
        return
      }
      let error = null
      // @ts-ignore
      const session = new ApplePaySession(3, {
        countryCode: 'GB',
        currencyCode: 'GBP',
        supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
        merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
        total: { label: 'RKings Competitions', amount: total },
      })
      if (isCart) {
        handleSubmit(data => {
          setLoading(true)
          axios
            .post(API_URL.checkout, {
              cart_id: cartId,
              address: data?.address,
              payment: { gateway: isBarclays ? 'barclaycard_apple_pay' : 'checkout_apple_pay', amount: total, currency: 'GBP' }, // gateway from useStats
            })
            .then(res => {
              let paymentId = res?.data?.data?.payment_id
              let paymentToken = res?.data?.data?.token
              if (res?.data?.code == 0 && paymentId && paymentToken) {
                // eslint-disable-next-line no-unused-vars
                session.onvalidatemerchant = event => session.completeMerchantValidation(JSON.parse(paymentToken))
                session.onpaymentauthorized = event => {
                  const paymentDataBase64 = isBarclays ? getPaymentData(event) : event.payment.token // useStats, barclay: base64, checkout: event.payment.token
                  let error2 = null
                  axios
                    .post(isBarclays ? API_URL.applePayCbBarclays : API_URL.applePayCbCheckout, { id: paymentId, apple_auth: paymentDataBase64 })
                    .then(res2 => {
                      const url = res2?.data?.data?.url ?? (res2?.data?.data?.order_id ? '/cart/' + res2.data.data.order_id : null)
                      if (url) {
                        // @ts-ignore
                        session.completePayment(ApplePaySession.STATUS_SUCCESS)
                        router.push(url)
                        toast.success(cartStr.success, { toastId: 'apple-pay' })
                      } else {
                        error2 = { response: res2 }
                      }
                    })
                    .catch(err => (error2 = err))
                    .finally(() => {
                      paymentId = null
                      paymentToken = null
                      setDisabled(false)
                      if (error2) {
                        // @ts-ignore
                        session.completePayment(ApplePaySession.STATUS_FAILURE)
                        toast.error(appUtils.handleApiError(error2, errStr), { toastId: 'apple-pay' })
                      }
                    })
                }
                session.oncancel = event => {
                  console.log('Payment canceled by user', event)
                }
                session.begin()
              } else {
                error = { response: res }
              }
            })
            .catch(err => (error = err))
            .finally(() => {
              setLoading(false)
              reset()
              if (error) {
                evt?.preventDefault?.()
                session?.abort?.()
                const data = error.response?.data?.data
                if (data) {
                  setCartDetail(data)
                  updateCartLocal(data)
                }
                toast.error(appUtils.handleApiError(error, errStr), { toastId: 'apple-pay' })
                return
              }
            })
        })()
      } else {
        // eslint-disable-next-line no-unused-vars
        session.onvalidatemerchant = event => axios.get(API_URL.cartValidate).then(res => session.completeMerchantValidation(res?.data))
        session.onpaymentauthorized = event => {
          const paymentDataBase64 = isBarclays ? getPaymentData(event) : event.payment.token // useStats, barclay: base64, checkout: event.payment.token
          handleSubmit(data => {
            setDisabled(true)
            axios
              .post(API_URL.topUp, {
                address: data?.address,
                payment: {
                  gateway: isBarclays ? 'barclaycard_apple_pay' : 'checkout_apple_pay',
                  apple_auth: paymentDataBase64,
                  amount: total,
                  currency: 'GBP',
                }, // gateway from useStats
                amount: total,
                currency: 'GBP',
              })
              .then(res => {
                if (res?.data?.code == '0') {
                  // @ts-ignore
                  session.completePayment(ApplePaySession.STATUS_SUCCESS)
                  if (res?.data?.data?.url) {
                    router.push(res.data.data.url)
                  } else if (res?.data?.data?.id) {
                    router.push('/cart/' + res.data.data.id)
                  } else {
                    router.reload()
                  }
                  toast.success(walletStr.top_up_success, { toastId: 'apple-pay' })
                } else {
                  error = { response: res }
                }
              })
              .catch(err => (error = err))
              .finally(() => {
                setDisabled(false)
                if (error) {
                  // @ts-ignore
                  session.completePayment(ApplePaySession.STATUS_FAILURE)
                  toast.error(appUtils.handleApiError(error, errStr), { toastId: 'apple-pay' })
                }
              })
          })()
        }
        session.begin()
      }
    },
    [total, form]
  )

  return (
    <div ref={wrapRef} className={`transition-opacity ease-in ${isSupported ? extraCls : 'hidden'}`}>
      {/* @ts-ignore */}
      <apple-pay-button
        ref={shadowRef}
        buttonstyle='black'
        type='pay'
        locale='en-GB'
        onClick={onClick}
        id='rkApplePay'
        key={'rkApplePay' + (cartId ?? '') + '-' + total}
        style={style}
        // eslint-disable-next-line jsx-a11y/aria-props
        aria-grey={disabled} // change btn style
        aria-disabled={formInvalid} // won't affect btn style
      />
    </div>
  )
}

export default ApplePayButton
